import React, { useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, Select, Button, Modal, Skeleton } from "antd";
import moment from "moment";
import { connect } from "react-redux";
import {
  getLocations,
  getIsOnBreak,
  getIsCheckedIn,
  getCheckInLocationId,
  getPunchId,
  getBreakId,
  getShiftTimeMaster,
  getTime24,
  getDepartmentList,
  getAllDepartmentList,
  getUserInfo,
  getLongDay,
  getUserList,
  getClientid,
  getSelectedDepartmentId,
  getUserRoleId,
  getRoleList,
  getCheckInDepartmentId,
} from "redux/selectors";
import { bindActionCreators } from "redux";
import {
  setIsOnBreak,
  toggleSuccessModal,
  setCheckedIn,
  setCheckInLocationId,
  toggleProcessingModal,
  setPunchId,
  setBreakId,
  setCheckInDepartmentId,
} from "redux/actions";
import styled from "@emotion/styled";
import COLORS from "common/Colors";
import CustomIcon from "atoms/Icon";
import BreakModal from "organisms/BreakModal";
import CheckInProblemModal from "organisms/CheckInProblemModal";
import { displayErrorModal } from "common/common";
import { PUNCH_ACTIONS, STATUS_CODE } from "common/Constants";
import { CALL_API, CALL_FEATCH_API, FETCH_FULL_USER_LIST } from "common/API";
import posthog from "analytics/posthog";
import { find, get, includes, size, orderBy } from "lodash";
import { DEVICE } from "common/Device";
import { dateInDetail, getMySqlDate, getKenyanDateTime } from "utils/Date";
import { Input } from "antd";
import { ButtonFilled } from "reusableComponent/UIButtons";
import { jwtDecode } from "jwt-decode";

// Location id of "Others" - always sends the user's real current coordinates
const OTHERS_LOCATION_ID = "6";

// ---------------------------------------------------------------------
// Geolocation tuning (FAST)
// ---------------------------------------------------------------------
const GEO_TARGET_ACCURACY_M = 150; // finish immediately on a fix this good
const GEO_MAX_ACCEPTABLE_ACCURACY_M = 5000; // reject anything worse than this
const GEO_GRACE_MS = 3000; // after the FIRST usable fix, wait at most this long for a better one
const GEO_HARD_CAP_MS = 10000; // absolute maximum time for the whole geolocation step
// First check-in after login usually shows the browser's "Allow location"
// prompt - give the user time to answer it before we give up.
const GEO_PERMISSION_PROMPT_CAP_MS = 60000;

// Max time we wait for the punch-in-out API before giving up
const PUNCH_API_TIMEOUT_MS = 30000;

// Max time the Check-In / Check-Out button stays as a skeleton while the
// bootstrap APIs load. After this we show it anyway - the click itself
// re-fetches whatever is still missing (see ensurePunchContext).
const BUTTON_READY_MAX_WAIT_MS = 20000;

// master-data (shift times + open punch) is retried this many times before
// we give up and ask the user to refresh
const RESTORE_MAX_ATTEMPTS = 3;
const RESTORE_RETRY_DELAY_MS = 3000;

const PROFILE_NOT_READY_MESSAGE =
  "We are still loading your profile details. Please wait a moment and try again.";

// Wrap any promise with a timeout so the UI can never hang forever
const withTimeout = (promise, ms, message = "Request timed out") =>
  new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });

// ---------------------------------------------------------------------
// Check-in cache: lets the correct button render on the very first paint
// after a hard refresh, without waiting for any API.
// ---------------------------------------------------------------------
const CACHE_KEY = "checkin_state_v1";
const CACHE_MAX_AGE_HOURS = 24;

const readCheckInCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c?.id) return null;
    if (moment().diff(moment(c.savedAt), "hours") >= CACHE_MAX_AGE_HOURS) {
      window.localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return c;
  } catch (e) {
    return null;
  }
};

const writeCheckInCache = (data) => {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ...data, savedAt: new Date().toISOString() })
    );
  } catch (e) {
    /* ignore */
  }
};

const clearCheckInCache = () => {
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch (e) {
    /* ignore */
  }
};

// Case/whitespace-insensitive string compare (emails etc.)
const sameStr = (a, b) =>
  String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

/**
 * Gets a position FAST, as accurate as time allows.
 *
 * Strategy:
 *  1. Fire a quick low-accuracy getCurrentPosition (network/Wi-Fi) so we
 *     almost always have *some* fix within a second or two.
 *  2. At the same time run a high-accuracy watchPosition (GPS) for a better fix.
 *  3. Finish when:
 *       - accuracy <= GEO_TARGET_ACCURACY_M, OR
 *       - GEO_GRACE_MS has passed since the first usable fix, OR
 *       - GEO_HARD_CAP_MS has passed (hard stop).
 *  4. Permission denied (code 1) stops immediately.
 */
const getBestBrowserPosition = (hardCapMs = GEO_HARD_CAP_MS) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({ code: 0, message: "Geolocation not supported" });
      return;
    }

    let best = null;
    let lastError = null;
    let finished = false;
    let watchId = null;
    let hardTimer = null;
    let graceTimer = null;

    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(hardTimer);
      clearTimeout(graceTimer);
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (best) resolve(best);
      else reject(lastError || { code: 3, message: "Location request timed out" });
    };

    const onFix = (pos) => {
      if (finished) return;
      console.log(
        "[geo] fix:",
        pos.coords.latitude,
        pos.coords.longitude,
        "accuracy(m):",
        pos.coords.accuracy
      );
      if (!best || pos.coords.accuracy < best.coords.accuracy) {
        best = pos;
      }
      if (pos.coords.accuracy <= GEO_TARGET_ACCURACY_M) {
        finish();
        return;
      }
      // First usable fix: start a short grace window for a better one
      if (!graceTimer) {
        graceTimer = setTimeout(finish, GEO_GRACE_MS);
      }
    };

    const onError = (error) => {
      lastError = error;
      // Permission denied won't recover - stop immediately
      if (error?.code === 1) finish();
    };

    // 1) Quick coarse fix (allows a recent cached position - it's only a fallback)
    navigator.geolocation.getCurrentPosition(onFix, onError, {
      enableHighAccuracy: false,
      maximumAge: 10000,
      timeout: hardCapMs,
    });

    // 2) Better fix via GPS
    watchId = navigator.geolocation.watchPosition(onFix, onError, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: hardCapMs,
    });

    // 3) Absolute hard cap
    hardTimer = setTimeout(finish, hardCapMs);
  });

// Longer cap while the browser is still going to ask for location permission
const getGeoHardCapMs = async () => {
  try {
    const status = await navigator.permissions?.query({ name: "geolocation" });
    if (status?.state === "prompt") return GEO_PERMISSION_PROMPT_CAP_MS;
  } catch (e) {
    /* Permissions API not supported - use the default */
  }
  return GEO_HARD_CAP_MS;
};

const StyleContainerRow = styled(Row)`
  max-height: 115px;
  min-height: 100px;
  box-shadow: 0px 4px 30px 0px #0000001f;
  border-radius: 8px;
  // ORIGINAL GRADIENT (kept for reference, currently disabled)
  // background: transparent
  //   linear-gradient(98deg, #f2f6ff -30%, ${COLORS.PRIMARY} 100%) 0% 0% no-repeat
  //   padding-box;

  // NEW: solid mint-green background
  background:#F3FEFA;

  padding: 7px 15px;
  margin-bottom: 16px;

  @media (max-width: 576px) {
    max-height: 200px;
    margin-left: 0px;
  }

  @media (max-width: 376px) {
    max-height: 220px;
  }
`;

const StyledCheckInCheckOutButton = styled(Button)`
  margin-left: 5px;
  margin-right: 5px;
  border-radius: 5px;
  width: 160px;
  :hover {
    color: #000 !important;
    border-color: #c2fae5 !important;
  }
  @media (max-width: 321px) {
    margin-bottom: 0px;
  }
  @media ${DEVICE.mobileS} {
    margin-bottom: 5px;
    margin-left: 0px;
  }
`;

const StyledStartBreak = styled(Button)`
  margin-right: 20px;
  font-size: 1rem;
  vertical-align: text-top;
  /* margin-top: 5px; */
  svg {
    margin-left: 8px;
    vertical-align: top;
  }
  @media (max-width: 376px) {
    font-size: 1rem;
    margin-left: -15px;
  }
`;
const StyledLocationSelect = styled(Select)`
  @media ${DEVICE.tablet} {
    width: initial;
  }
  @media (max-width: 575px) {
    width: 120%;
  }
  @media (max-width: 426px) {
    width: 100%;
  }
  @media (max-width: 416px) {
    width: 100%;
  }
  margin-right: 15px;
  width: 160px;
`;

function CheckInCheckout({
  locationList,
  isOnBreak,
  isCheckedIn,
  updateIsOnBreak,
  updateSuccessModal,
  updateCheckedIn,
  updateCheckInLocationId,
  checkInLocationId,
  updateProcessingModal,
  punchId,
  breakId,
  savePunchId,
  saveBreakId,
  shiftTimeMaster,
  userInfo,
  userList,
  ClientId,
  userRoleId,
  roleList,
  AllDepartmentList,
  checkInDepartmentId,
  updateCheckInDepartmentId,
}) {
  const [isBreakModalVisible, toggleDisplayBreakModal] = useState(false);
  // { action, title, message } of the last failed punch, or null
  const [checkInProblem, setCheckInProblem] = useState(null);
  const [isCheckInReminderModalVisible, toggleCheckInReminderModal] =
    useState(false);

  // ---------------------------------------------------------------------
  // Local, sticky check-in state (single source of truth for the buttons).
  // Initialised synchronously from the cache so the right button is there
  // on the very first render after a hard refresh.
  // ---------------------------------------------------------------------
  const [restoredPunch, setRestoredPunch] = useState(() => {
    const c = readCheckInCache();
    return c ? { id: c.id, locationId: c.locationId, userid: c.userid } : null;
  });

  // When true, Redux is NOT allowed to flip us back to "checked in"
  // (set after a check-out or when the server says there is no open punch)
  const blockReduxUpgrade = useRef(false);

  const [locationId, setLocationId] = useState(() => {
    const c = readCheckInCache();
    return c?.locationId ? String(c.locationId) : null;
  });
  const [departmentId, setDepartmentId] = useState(0);
  const [showLateCheckInModal, setShowLateCheckInModal] = useState(false);
  const [punchInOutReason, setPunchInOutReason] = useState("");
  const [reasonSubmited, setReasonSubmited] = useState(false);
  const [reasonValidation, setReasonValidation] = useState(false);
  const containerRef = useRef(null);
  const { TextArea } = Input;

  // Prevent double-clicks from firing two punch requests
  const isSubmittingRef = useRef(false);

  // Final, stable flags - never depend on Redux directly
  const checkedIn = !!restoredPunch;
  const effectivePunchId = restoredPunch?.id || "";

  // Drop a cached punch that belongs to a different user
  useEffect(() => {
    if (
      restoredPunch?.userid &&
      userInfo?.userid &&
      !sameStr(restoredPunch.userid, userInfo.userid)
    ) {
      clearCheckInCache();
      setRestoredPunch(null);
      setLocationId(null);
    }
  }, [restoredPunch?.userid, userInfo?.userid]);

  // Redux may only UPGRADE us to checked-in (when the cache was empty).
  // It can never downgrade us - only check-out / the server can.
  useEffect(() => {
    if (blockReduxUpgrade.current) return;
    if (restoredPunch) return;
    if (isCheckedIn && punchId) {
      setRestoredPunch({
        id: punchId,
        locationId: checkInLocationId ? String(checkInLocationId) : "",
        userid: userInfo?.userid,
      });
    }
  }, [isCheckedIn, punchId, checkInLocationId, restoredPunch, userInfo?.userid]);

  // ---------------------------------------------------------------------
  // Current user record (case-insensitive userid match)
  // ---------------------------------------------------------------------
  const currentUser = find(userList, (u) => sameStr(u?.userid, userInfo?.userid));

  // Client id is in the login token, so the check-in API calls can start on
  // the first render instead of waiting for the app-level bootstrap.
  const effectiveClientId = useMemo(() => {
    if (ClientId) return ClientId;
    try {
      return jwtDecode(window.localStorage.getItem("token"))?.clientId;
    } catch (e) {
      return undefined;
    }
  }, [ClientId]);

  // Shift times come from the master-data API response
  // (usershiftstarttime / usershiftendtime, e.g. "08:00" / "17:00")
  const [shiftTimes, setShiftTimes] = useState({ start: "", end: "" });
  const shiftTimesLoadedRef = useRef(false);

  // Never undefined - JSON.stringify drops undefined keys from the payload
  const shiftStartTime = shiftTimes.start ?? "";
  const shiftEndTime = shiftTimes.end ?? "";

  // User record fetched on demand when the app-level userList hasn't
  // arrived yet at click time (see ensurePunchContext)
  const fallbackUserRef = useRef(null);

  const [locationError, setLocationError] = useState(false);
  const [departmentError, setDepartmentError] = useState(false);

  // Compare as strings to avoid "1" vs 1 mismatches
  const filterRoleList = (roleList || []).filter(
    (item) => String(item?.roleId) === String(userRoleId)
  );

  // ---------------------------------------------------------------------
  // Location options fetched from the owner-location API
  // ---------------------------------------------------------------------
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  useEffect(() => {
    if (!effectiveClientId) return;
    let cancelled = false;

    const fetchOwnerLocations = async () => {
      setLocationsLoading(true);
      try {
        const res = await CALL_FEATCH_API(`owner-location/${effectiveClientId}`, "get");
        if (cancelled) return;
        const list = res?.response?.response ?? res?.response ?? [];
        if (Array.isArray(list)) {
          setLocationOptions(
            orderBy(list, [(loc) => Number(loc.location_id)], ["asc"])
          );
        }
        // If the response isn't an array we keep whatever we already had
      } catch (err) {
        // Do NOT wipe already-loaded options on a failed refetch
        console.error("[owner-location] error:", err);
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    };

    fetchOwnerLocations();

    return () => {
      cancelled = true;
    };
  }, [effectiveClientId]);

  // Options for the Select: always string values so "1" vs 1 never mismatches
  const selectOptions = [
    // { location_id: "", location_name: "Select Location" },
    ...locationOptions.map((loc) => ({
      ...loc,
      location_id: String(loc.location_id),
    })),
  ];

  // Only pass a value to the Select if we can actually show its NAME.
  // Otherwise antd renders the raw id (e.g. "1") until options arrive.
  const selectValue =
    !locationsLoading &&
      locationId &&
      locationOptions.some((l) => String(l.location_id) === String(locationId))
      ? String(locationId)
      : undefined;

  useEffect(() => {
    if (locationList?.length > 0) {
      console.log(locationList, "locationList READY");
    }
  }, [locationList]);

  // ---------------------------------------------------------------------
  // Keep local selection in sync with Redux - but NEVER blank it out when
  // Redux is momentarily reset to an empty value by another bootstrap call.
  // (Explicit clearing happens on check-out below.)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (checkInLocationId) {
      setLocationId(String(checkInLocationId));
    }
  }, [checkInLocationId]);

  useEffect(() => {
    if (checkInDepartmentId) {
      setDepartmentId(checkInDepartmentId);
    }
  }, [checkInDepartmentId]);

  // If we're checked in at a location and nothing is selected yet, show it.
  useEffect(() => {
    if (restoredPunch?.locationId && !locationId) {
      setLocationId(String(restoredPunch.locationId));
    }
  }, [restoredPunch, locationId]);

  // ---------------------------------------------------------------------
  // Department resolution: role -> user record fallback
  // ---------------------------------------------------------------------
  const filterDepartmentId = filterRoleList[0]?.department;
  const filterDepartment = (AllDepartmentList || []).filter((item) =>
    filterDepartmentId?.includes(item?.department_id)
  );

  // Adjust `currentUser?.departmentid` to the real field name on your user record
  const resolvedDepartment =
    filterDepartment[0] ||
    find(
      AllDepartmentList,
      (d) => String(d?.department_id) === String(currentUser?.departmentid)
    ) ||
    {};

  // ---------------------------------------------------------------------
  // Button readiness: the punch payload needs the user id/name, the user
  // record (employee id, supervisor, base location, country), the shift
  // times and the server's punch state. On a slow network these arrive
  // seconds after login, and punching before then sends empty fields - the
  // backend then fails on the empty userid and the user sees
  // "Problem checking in". The button stays DISABLED until every one of
  // them is here; it is never enabled with a partial payload.
  //
  // App.js stores userList and the rest of master-data (roles, departments,
  // locations, userInfo) in the same step, so a non-empty userList means
  // that whole bootstrap has finished.
  // ---------------------------------------------------------------------
  const [restoreDone, setRestoreDone] = useState(false);
  // True only once master-data actually succeeded (shift times + punch state)
  const [punchStateLoaded, setPunchStateLoaded] = useState(false);
  const [readyWaitExpired, setReadyWaitExpired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pendingForPunch = [
    !effectiveClientId && "clientId",
    !userInfo?.userid && "userId",
    !userInfo?.name && "userName",
    !size(userList) && "appBootstrap",
    size(userList) > 0 && !currentUser && "userRecord",
    locationsLoading && "ownerLocations",
    !punchStateLoaded && "punchState",
  ].filter(Boolean);
  const isPunchReady = pendingForPunch.length === 0;

  // Skeleton only until we know Check-In vs Check-Out; after that the
  // button is shown but stays disabled until the payload is complete.
  const showPunchButtons = restoreDone;

  // Bootstrap finished but this user isn't in the roster - waiting won't
  // help, so tell the user instead of leaving a silently disabled button.
  const punchBlockedMessage = !restoreDone
    ? ""
    : !punchStateLoaded
      ? "Could not load your check-in details. Please refresh the page."
      : includes(pendingForPunch, "userRecord")
        ? "Your employee profile could not be found. Please contact your administrator."
        : "";

  useEffect(() => {
    if (isPunchReady) return;
    const t = setTimeout(() => setReadyWaitExpired(true), BUTTON_READY_MAX_WAIT_MS);
    return () => clearTimeout(t);
  }, [isPunchReady]);

  // Log what we were still waiting on when the cap hit, to find slow APIs
  useEffect(() => {
    if (readyWaitExpired && !isPunchReady) {
      reportPunchProblem("button_ready", {
        reason: "READY_WAIT_EXPIRED",
        pending: pendingForPunch,
      });
    }
  }, [readyWaitExpired]);

  // ---------------------------------------------------------------------
  // Reconcile with the server after a hard refresh. The cache decides
  // Check-In vs Check-Out; this corrects it if the server disagrees.
  // Runs on the first render (client id from the token, user id from the
  // response) instead of waiting for the app-level bootstrap to finish.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!effectiveClientId) return;

    let cancelled = false;

    const restorePunchState = async (attempt = 1) => {
      let loaded = false;
      try {
        const res = await CALL_API(`master-data/${effectiveClientId}`, "post", {});
        if (cancelled) return;
        if (res?.code !== STATUS_CODE.SUCCESS) return;

        const restoreUserid = res.userid || userInfo?.userid;
        if (!restoreUserid) return;
        loaded = true;

        console.log("[restore] punches:", res?.userPunchactionList);

        // Shift times from master-data
        setShiftTimes({
          start: res.usershiftstarttime ?? "",
          end: res.usershiftendtime ?? "",
        });
        shiftTimesLoadedRef.current = true;

        const openPunch = orderBy(
          (res.userPunchactionList || []).filter(
            (p) =>
              sameStr(p.userid, restoreUserid) &&
              !p.checkoutdatetime &&
              Number(p.punchoutid) !== 2
          ),
          ["checkindatetime"],
          ["desc"]
        )[0];

        if (openPunch) {
          const restoredLocationId = String(openPunch.loginlocationid);
          blockReduxUpgrade.current = false;
          setRestoredPunch({
            id: openPunch.userpunchactionid,
            locationId: restoredLocationId,
            userid: restoreUserid,
          });
          setLocationId(restoredLocationId);
          writeCheckInCache({
            id: openPunch.userpunchactionid,
            locationId: restoredLocationId,
            userid: restoreUserid,
          });
          updateCheckedIn(true);
          savePunchId(openPunch.userpunchactionid);
          updateCheckInLocationId(restoredLocationId);
        } else {
          // Server says: not checked in. Correct a stale cache.
          blockReduxUpgrade.current = true;
          clearCheckInCache();
          setRestoredPunch(null);
        }
      } catch (err) {
        // Keep whatever the cache says
        console.error("[restore] error:", err);
      } finally {
        if (!cancelled) {
          if (loaded) {
            setPunchStateLoaded(true);
            setRestoreDone(true);
          } else if (attempt < RESTORE_MAX_ATTEMPTS) {
            // Transient failure (e.g. Lambda cold start) - try again
            setTimeout(() => {
              if (!cancelled) restorePunchState(attempt + 1);
            }, RESTORE_RETRY_DELAY_MS);
          } else {
            setRestoreDone(true);
          }
        }
      }
    };

    restorePunchState();

    return () => {
      cancelled = true;
    };
  }, [effectiveClientId]);

  // Sync Redux ONCE per restored punch (not on every Redux change), so we
  // never get into a fight loop with whatever resets Redux.
  useEffect(() => {
    if (!restoredPunch?.id) return;
    updateCheckedIn(true);
    savePunchId(restoredPunch.id);
    if (restoredPunch.locationId) {
      updateCheckInLocationId(restoredPunch.locationId);
    }
  }, [restoredPunch?.id]);

  useEffect(() => { }, [punchInOutReason, reasonSubmited]);

  const getPunchInOutReason = (action) => {
    if (punchInOutReason != "") {
      setReasonSubmited(true);
      setShowLateCheckInModal(false);
      setReasonValidation(false);
      punchInOutApiCal(action, "");
    } else {
      setReasonValidation(true);
    }
  };
  const handlePunchInOutReason = (e) => {
    setPunchInOutReason(e.target.value);
    if (e.target.value.length > 1) {
      setReasonValidation(false);
    }
  };
  const hideModal = () => {
    setShowLateCheckInModal(false);
    setPunchInOutReason("");
  };
  const startBreak = (breakTypeId) => {
    toggleDisplayBreakModal(false);
    punchInOutApiCal(PUNCH_ACTIONS.BREAK_START, breakTypeId);
  };

  const getCoordinates = async (action) => {
    const inOut = action === PUNCH_ACTIONS.PUNCH_IN ? "in" : "out";

    return new Promise((resolve) => {
      try {
        if (navigator.userAgent === "application") {
          // Native app wrapper: coordinates are pushed into localStorage by the app.
          // Ask the app for a fresh fix first, then read after a short delay.
          try {
            window.Print?.postMessage("access-location");
          } catch (e) {
            /* ignore */
          }
          setTimeout(() => {
            const latitude = window.localStorage.getItem("latitude");
            const longitude = window.localStorage.getItem("longitude");
            if (!latitude || !longitude) {
              updateProcessingModal(false);
              displayErrorModal(
                `Please allow Geo Location to read your browser location for ${inOut}`
              );
              resolve({ latitude: "", longitude: "" });
            } else {
              resolve({ latitude, longitude });
            }
          }, 1000);
        } else {
          getGeoHardCapMs()
            .then((hardCapMs) => getBestBrowserPosition(hardCapMs))
            .then((pos) => {
              const { latitude, longitude, accuracy } = pos.coords;
              console.log("[geo] using:", latitude, longitude, "accuracy(m):", accuracy);

              // Too coarse (usually IP-based) -> don't send a wrong location
              if (accuracy > GEO_MAX_ACCEPTABLE_ACCURACY_M) {
                updateProcessingModal(false);
                displayErrorModal(
                  "Your device could not get an accurate location (it looks like an approximate, network-based location). Please turn on Location Services / Wi-Fi, or try from your phone, and try again."
                );
                resolve({ latitude: "", longitude: "" });
                return;
              }

              resolve({ latitude, longitude });
            })
            .catch((error) => {
              updateProcessingModal(false);
              if (error?.code === 1) {
                displayErrorModal(
                  `Please allow Geo Location to read your browser location for Check ${inOut}`
                );
              } else {
                displayErrorModal("Unable to read browser location.");
              }
              resolve({ latitude: "", longitude: "" });
            });
        }
      } catch (err) {
        updateProcessingModal(false);
        displayErrorModal("Unable to read browser location.");
        resolve({
          latitude: "",
          longitude: "",
        });
      }
    });
  };

  const fetchMasterData = async (fetch_master_data) => {
    try {
      const { code, department_id, usershiftstarttime, usershiftendtime } =
        await CALL_API(`master-data/${ClientId}`, "post", {
          fetch_master_data,
        });
      if (code === STATUS_CODE.SUCCESS) {
        updateCheckInDepartmentId(department_id?.toString());
        setShiftTimes({
          start: usershiftstarttime ?? "",
          end: usershiftendtime ?? "",
        });
        shiftTimesLoadedRef.current = true;
      }
    } catch (err) {
      console.error("[master-data] error:", err);
    }
  };

  // Right after login the app-level bootstrap (master-data, locations, user
  // info) may still be in flight. If the user clicks Check-In before it lands,
  // fetch what the punch needs here instead of sending empty values.
  const ensurePunchContext = async () => {
    let clientId = ClientId;
    if (!clientId) {
      try {
        clientId = jwtDecode(window.localStorage.getItem("token"))?.clientId;
      } catch (e) {
        /* ignore */
      }
    }
    const ctx = {
      clientId,
      userid: userInfo?.userid,
      name: userInfo?.name,
      locations: locationList,
      shiftStart: shiftStartTime,
      shiftEnd: shiftEndTime,
      user: currentUser || fallbackUserRef.current,
      department: resolvedDepartment,
    };
    if (!clientId) return ctx;

    const needsDepartment = !ctx.department?.department_id;
    if (
      !ctx.userid ||
      !size(ctx.locations) ||
      !shiftTimesLoadedRef.current ||
      needsDepartment
    ) {
      try {
        const res = await CALL_API(
          `master-data/${clientId}`,
          "post",
          needsDepartment ? { fetch_master_data: true } : {}
        );
        if (res?.code === STATUS_CODE.SUCCESS) {
          ctx.userid = ctx.userid || res.userid;
          ctx.name = ctx.name || res.name;
          if (!size(ctx.locations)) ctx.locations = res.locations || [];
          if (!shiftTimesLoadedRef.current) {
            ctx.shiftStart = res.usershiftstarttime ?? "";
            ctx.shiftEnd = res.usershiftendtime ?? "";
            setShiftTimes({ start: ctx.shiftStart, end: ctx.shiftEnd });
            shiftTimesLoadedRef.current = true;
          }
          if (needsDepartment && size(res.departmentList)) {
            const roleId = userRoleId || res.role_id?.[0];
            const role = find(
              res.roleList,
              (r) => String(r?.roleId) === String(roleId)
            );
            ctx.department =
              find(res.departmentList, (d) =>
                role?.department?.includes(d?.department_id)
              ) ||
              find(
                res.departmentList,
                (d) =>
                  String(d?.department_id) ===
                  String(ctx.user?.departmentid ?? res.department_id)
              ) ||
              ctx.department;
          }
        }
      } catch (err) {
        console.error("[punch-context] master-data error:", err);
      }
    }

    // The app-level userList is the slowest bootstrap call - fetch it here
    // if it hasn't landed (or doesn't contain this user) yet.
    if (!ctx.user && ctx.userid) {
      try {
        const { userList: freshList } = (await FETCH_FULL_USER_LIST(clientId)) || {};
        ctx.user =
          find(freshList, (u) => sameStr(u?.userid, ctx.userid)) || null;
        fallbackUserRef.current = ctx.user;
      } catch (err) {
        console.error("[punch-context] user-list error:", err);
      }
    }
    return ctx;
  };

  const reportPunchProblem = (action, details) => {
    console.error("[punch-in-out] problem:", { action, ...details });
    try {
      posthog.capture("checkin_problem", { action, ...details });
    } catch (e) {
      /* analytics must never break the punch flow */
    }
  };

  const showPunchProblem = (action, message) => {
    setCheckInProblem({
      action,
      title:
        action === PUNCH_ACTIONS.PUNCH_OUT
          ? "Problem checking out"
          : "Problem checking in",
      message,
    });
  };

  const punchInOutApiCal = async (action, breakTypeId) => {
    if (isSubmittingRef.current) return; // ignore double clicks while in progress
    // Check-In / Check-Out must never go out with a partial payload
    const isPunchAction = includes(
      [PUNCH_ACTIONS.PUNCH_IN, PUNCH_ACTIONS.PUNCH_OUT],
      action
    );
    if (isPunchAction && !isPunchReady) return;

    if (!locationId) {
      setLocationError(true);
      return;
    }
    setLocationError(false);
    // if (!departmentId) {
    //   setDepartmentError(true);
    //   return;
    // }
    // setDepartmentError(false);

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    const tStart = Date.now();

    try {
      updateProcessingModal(true);
      const ctx = await ensurePunchContext();

      // Never send a punch with an empty profile - the backend rejects it
      // and the user gets a confusing error. Ask them to retry instead.
      const missing = [
        !ctx.clientId && "clientId",
        !ctx.userid && "userid",
      ].filter(Boolean);
      if (missing.length) {
        reportPunchProblem(action, { reason: "PROFILE_NOT_READY", missing });
        updateProcessingModal(false);
        displayErrorModal(PROFILE_NOT_READY_MESSAGE);
        return;
      }
      // Not blocking: a user missing from the roster would never be able to
      // punch. Send it (as before) and record it so the data can be fixed.
      if (!ctx.user) {
        reportPunchProblem(action, { reason: "USER_RECORD_NOT_FOUND", userid: ctx.userid });
      }

      const selectedLocation = find(
        locationOptions,
        (loc) => String(loc.location_id) === String(locationId)
      );
      const loginFromAnywhere = get(
        find(
          ctx.locations,
          (loc) => String(loc.location_id) === String(locationId)
        ),
        "login_from_anywhere",
        0
      );
      const isPunchInOut = includes(
        [PUNCH_ACTIONS.PUNCH_IN, PUNCH_ACTIONS.PUNCH_OUT],
        action
      );

      // "Others" must always send the user's real current coordinates
      const isOthers =
        /^other/i.test(selectedLocation?.location_name || "") ||
        String(locationId) === OTHERS_LOCATION_ID;

      let latitude = "";
      let longitude = "";

      if (isPunchInOut && (isOthers || !loginFromAnywhere)) {
        ({ latitude = "", longitude = "" } = await getCoordinates(action));
        console.log("[timing] geolocation took (ms):", Date.now() - tStart);
      } else {
        latitude = -1.313744;
        longitude = 36.8584;
      }

      if (
        (isPunchInOut && latitude !== "" && longitude !== "") ||
        !isPunchInOut
      ) {
        // Location name comes from the owner-location API list
        const locationName = selectedLocation;

        const tApi = Date.now();
        const punchUser = ctx.user;
        const {
          code,
          message: serverMessage,
          maxProximity = "",
          punchActionInsertedId = 0,
          breakActionInsertedId = 0,
        } = await withTimeout(
          CALL_API(`punch-in-out/${ctx.clientId}`, "post", {
            breaktypeid: breakTypeId,
            punchinid: effectivePunchId,
            punchid: action,
            departmentname: ctx.department?.department_name ?? "",
            departmentid: ctx.department?.department_id ?? "",
            fullname: ctx.name,
            userid: ctx.userid,
            breakid: breakId,
            loginlocationid: action === 1 ? parseInt(locationId) : "",
            loginlocationname: action === 1 ? locationName?.location_name : "",
            loginlatitude: action === 1 ? latitude : "",
            loginlongitude: action === 1 ? longitude : "",
            logoutlocationid: action === 2 ? parseInt(locationId) : "",
            logoutlocationname: action === 2 ? locationName?.location_name : "",
            logoutlatitude: action === 2 ? latitude : "",
            logoutlongitude: action === 2 ? longitude : "",
            skipdistancechecking: loginFromAnywhere || isOthers ? true : false,
            reason: punchInOutReason,
            shiftstarttime: ctx.shiftStart,
            shiftendtime: ctx.shiftEnd,
            employeeid: punchUser?.employeeid ?? "",
            officesupervisorid:
              punchUser?.OfficeSupervisorId === "none"
                ? punchUser?.employeeid
                : punchUser?.OfficeSupervisorId ?? "",
            officesupervisorname:
              punchUser?.officesupervisor === "none"
                ? punchUser?.fullname
                : punchUser?.officesupervisor ?? "",
            baselocationid: punchUser?.baselocationid ?? "",
            baselocationname:
              ctx.locations?.find(
                (loc) => loc.location_id === punchUser?.baselocationid
              )?.location_name ?? "",
            country: punchUser?.managingoffice?.[0] ?? "",
          }),
          PUNCH_API_TIMEOUT_MS,
          "punch-in-out API timed out"
        );
        console.log("[timing] punch-in-out API took (ms):", Date.now() - tApi);
        console.log("[timing] TOTAL (ms):", Date.now() - tStart);

        updateProcessingModal(false);
        if (code === STATUS_CODE.PUNCH_IN_SUCCESSFUL) {
          setPunchInOutReason("");
          blockReduxUpgrade.current = false;
          const newPunch = {
            id: punchActionInsertedId,
            locationId: String(locationId),
            userid: ctx.userid,
          };
          setRestoredPunch(newPunch);
          writeCheckInCache(newPunch);
          updateCheckedIn(true);
          updateCheckInLocationId(String(locationId));
          updateSuccessModal({
            visibility: true,
            message: `You have been checked in successfully`,
          });
          savePunchId(punchActionInsertedId);
          fetchMasterData();
        } else if (code === STATUS_CODE.PUNCH_OUT_SUCCESSFUL) {
          setPunchInOutReason("");
          // Block Redux from re-checking us in during the state transition
          blockReduxUpgrade.current = true;
          clearCheckInCache();
          setRestoredPunch(null);
          setLocationId(null);
          updateCheckedIn(false);
          updateCheckInLocationId("");
          updateSuccessModal({
            visibility: true,
            message: `You have been checked out successfully`,
          });
          savePunchId("");
          fetchMasterData();
        } else if (code === STATUS_CODE.BREAK_STARTED) {
          updateIsOnBreak(true);
          updateSuccessModal({
            visibility: true,
            message: "Break has been started",
          });
          saveBreakId(breakActionInsertedId);
        } else if (code === STATUS_CODE.REASON) {
          setReasonValidation(false);
          setShowLateCheckInModal(true);
        } else if (code === STATUS_CODE.BREAK_ENDED) {
          updateIsOnBreak(false);
          updateSuccessModal({
            visibility: true,
            message: "Your break has been stopped",
          });
          saveBreakId("");
        } else if (code === STATUS_CODE.RECORD_EXIST) {
          if (isPunchInOut) {
            displayErrorModal(
              `You are already logged ${action === PUNCH_ACTIONS.PUNCH_IN ? "in" : "out"
              }.`
            );
          } else {
            displayErrorModal(`You are already on break`);
          }
        } else if (code === STATUS_CODE.OUTSIDE_ALLOWED_PROXIMITY) {
          displayErrorModal(
            `You are far from allowed proximity (${maxProximity} meters). Please try to get closer.`
          );
        } else if (code === "USER_ON_LEAVE") {
              Modal.warning({
                title: "You are on leave",
                content: "You cannot check in or out.",
                okText: "OK",
                centered: true,
              });
            }
        else if (code === STATUS_CODE.INVALID_PAYLOAD) {
          reportPunchProblem(action, { reason: code, serverMessage });
          showPunchProblem(
            action,
            "Some of your profile details could not be loaded. Please refresh the page and try again."
          );
        } else {
          reportPunchProblem(action, { reason: "UNKNOWN_CODE", code, serverMessage });
          showPunchProblem(
            action,
            `Something went wrong. Please try again.${code ? ` (${code})` : ""}`
          );
        }
      }
    } catch (err) {
      reportPunchProblem(action, { reason: "REQUEST_FAILED", error: err?.message });
      showPunchProblem(
        action,
        "The network seems slow or unavailable. Please check your connection and try again."
      );
    } finally {
      // Always close the spinner and allow the next click
      updateProcessingModal(false);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const stopBreak = () => {
    Modal.confirm({
      icon: "",
      content: "Are you sure you want to stop the break?",
      okText: "YES",
      cancelText: "No",
      onOk: () => {
        punchInOutApiCal(PUNCH_ACTIONS.BREAK_END, breakId);
      },
    });
  };

  // ---------------------------------------------------------------------
  // Shift reminder - lives in an effect (not during render).
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!size(shiftTimeMaster)) return;
    if (effectivePunchId) return;
    if (isCheckInReminderModalVisible) return;
    if (window.localStorage.getItem("login_reminder") === "true") return;

    const kenyaDateTime = getKenyanDateTime();
    const dayIndex = dateInDetail(kenyaDateTime).day_index + 1;
    const { start_time, end_time } =
      find(shiftTimeMaster, { day: dayIndex }) || {};
    if (start_time && end_time) {
      const dayStartTime = moment(
        `${getMySqlDate(kenyaDateTime)} ${start_time}: 00`
      );
      const dayEndTime = moment(
        `${getMySqlDate(kenyaDateTime)} ${end_time}: 00`
      );
      const actualCheckInDateTime = moment(kenyaDateTime);
      if (actualCheckInDateTime < dayEndTime) {
        const diff = actualCheckInDateTime.diff(dayStartTime, "minutes");
        if (diff) {
          toggleCheckInReminderModal(true);
        }
      }
    }
  }, [shiftTimeMaster, effectivePunchId, isCheckInReminderModalVisible]);

  return (
    <>
      <StyleContainerRow>
        <Row>
          <Col xs={24} xl={12} md={12} sm={12}>
            <Col span={24}>
              <div>Location*</div>
            </Col>
            <Col md={16} sm={24} xs={24} ref={containerRef}>
              <StyledLocationSelect
                className="mt-1"
                getPopupContainer={() => containerRef.current}
                placeholder={locationsLoading ? "Loading..." : "Select Location"}
                loading={locationsLoading}
                options={selectOptions}
                fieldNames={{
                  label: "location_name",
                  value: "location_id",
                }}
                value={selectValue}
                onChange={(val) => setLocationId(val ? String(val) : null)}
                status={locationError ? "error" : ""}
                help={locationError ? "Location is required" : ""}
              />
            </Col>
          </Col>
          {/* Department select (disabled) kept out for brevity - restore from your original if needed */}
        </Row>

        <div
          className="d-flex justify-content-center gap-3 align-items-center"
          style={{ minHeight: 40 }}
        >
          {/* Skeleton until the data the punch needs has loaded */}
          {!showPunchButtons && (
            <Skeleton.Button
              className="mt-1"
              active
              style={{ width: 120, borderRadius: 5 }}
            />
          )}
          {showPunchButtons && !checkedIn && (
            <ButtonFilled
              className="mt-1"
              disabled={!isPunchReady || isSubmitting}
              loading={!isPunchReady && !punchBlockedMessage}
              onClick={() => punchInOutApiCal(PUNCH_ACTIONS.PUNCH_IN, "")}
            >
              Check-In
            </ButtonFilled>
          )}
          {showPunchButtons && checkedIn && !isOnBreak && (
            <ButtonFilled
              className="mt-1"
              disabled={!isPunchReady || isSubmitting}
              loading={!isPunchReady && !punchBlockedMessage}
              onClick={() => punchInOutApiCal(PUNCH_ACTIONS.PUNCH_OUT, "")}
            >
              Check-Out
            </ButtonFilled>
          )}
          {showPunchButtons && punchBlockedMessage && (
            <span className="mt-1" style={{ color: COLORS.ERROR, fontSize: 12 }}>
              {punchBlockedMessage}
            </span>
          )}
          {/* {checkedIn && (
            <StyledStartBreak
              type="text"
              onClick={() =>
                isOnBreak ? stopBreak() : toggleDisplayBreakModal(true)
              }
            >
              {isOnBreak ? "Stop Break" : "Start Break"}
              <CustomIcon name={isOnBreak ? "stop_break" : "play"} />
            </StyledStartBreak>
          )} */}
        </div>
        <BreakModal
          isModalVisible={isBreakModalVisible}
          closeModal={() => toggleDisplayBreakModal(!isBreakModalVisible)}
          startBreak={(breakTypeId) => {
            startBreak(breakTypeId);
          }}
        />
        <CheckInProblemModal
          isModalVisible={!!checkInProblem}
          title={checkInProblem?.title}
          message={checkInProblem?.message}
          closeModal={() => setCheckInProblem(null)}
          tryAgain={() => {
            const action = checkInProblem?.action ?? PUNCH_ACTIONS.PUNCH_IN;
            setCheckInProblem(null);
            punchInOutApiCal(action, "");
          }}
        />

        <Modal
          title=""
          open={showLateCheckInModal}
          footer={false}
          onCancel={hideModal}
          okText="Submit"
          cancelText="Cancel"
          maskClosable={false}
        >
          <div className="mt-3 mb-1">
            Reason For {!checkedIn ? "Late Check-in" : "Early Check-out"}
          </div>
          <TextArea
            rows={4}
            name="punchInOutReason"
            value={punchInOutReason}
            onChange={handlePunchInOutReason}
          />
          {reasonValidation && (
            <div className="text-danger">*Reason is required</div>
          )}
          <div className="mt-3 d-block">
            <div className="d-flex justify-content-end">
              <Button onClick={hideModal}>Cancel</Button>

              <Button
                type="primary"
                onClick={() =>
                  getPunchInOutReason(
                    !checkedIn
                      ? PUNCH_ACTIONS.PUNCH_IN
                      : PUNCH_ACTIONS.PUNCH_OUT
                  )
                }
                className="mx-2"
              >
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      </StyleContainerRow>
    </>
  );
}

const mapStateToProps = (state) => ({
  departmentList: getDepartmentList(state),
  locationList: getLocations(state),
  isOnBreak: getIsOnBreak(state),
  isCheckedIn: getIsCheckedIn(state),
  checkInLocationId: getCheckInLocationId(state),
  punchId: getPunchId(state),
  breakId: getBreakId(state),
  shiftTimeMaster: getShiftTimeMaster(state),
  systemTime24: getTime24(state),
  detaildate: getLongDay(state),
  userInfo: getUserInfo(state),
  userList: getUserList(state),
  ClientId: getClientid(state),
  selectedDepartmentId: getSelectedDepartmentId(state),
  AllDepartmentList: getAllDepartmentList(state),
  userRoleId: getUserRoleId(state),
  roleList: getRoleList(state),
  checkInDepartmentId: getCheckInDepartmentId(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateIsOnBreak: setIsOnBreak,
      updateCheckedIn: setCheckedIn,
      updateSuccessModal: toggleSuccessModal,
      updateCheckInLocationId: setCheckInLocationId,
      updateProcessingModal: toggleProcessingModal,
      savePunchId: setPunchId,
      saveBreakId: setBreakId,
      updateCheckInDepartmentId: setCheckInDepartmentId,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CheckInCheckout);